/**
 * "Download Android App" + "Install web app" (PWA) card, shown on the login
 * page and the homepage. Renders only when a genuine signed release is
 * published (latest.json), and never inside the Android app itself.
 */
import { useEffect, useState } from "react";
import { Download, Smartphone, MonitorDown, ShieldCheck } from "lucide-react";
import {
  fetchLatestAndroidRelease,
  formatBytes,
  isAndroidDevice,
  isIOSDevice,
  isNativeApp,
  isStandalonePWA,
} from "./platform";
import { canPromptInstall, onInstallAvailabilityChange, promptInstall } from "./pwa";
import "./appDownload.css";

export default function AppDownloadCard({ variant = "light", className = "" }) {
  const [release, setRelease] = useState(null);
  const [canInstall, setCanInstall] = useState(canPromptInstall());
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (isNativeApp()) return undefined;
    let alive = true;
    fetchLatestAndroidRelease()
      .then((r) => alive && setRelease(r))
      .catch(() => alive && setRelease(null));
    const off = onInstallAvailabilityChange(setCanInstall);
    return () => {
      alive = false;
      off();
    };
  }, []);

  if (isNativeApp()) return null;
  const showIOSHint = isIOSDevice() && !isStandalonePWA();
  if (!release && !canInstall && !showIOSHint) return null;

  const onAndroid = isAndroidDevice();

  return (
    <div className={`app-dl app-dl--${variant} ${className}`}>
      {release && (
        <>
          <a
            className="app-dl__primary"
            href={release.url}
            download={release.file}
            type="application/vnd.android.package-archive"
            onClick={() => setClicked(true)}
            data-testid="download-android-app"
          >
            <Download size={20} aria-hidden />
            <span className="app-dl__label">
              <strong>Download Android App</strong>
              <small>
                v{release.versionName} · {formatBytes(release.size)} · APK
              </small>
            </span>
          </a>

          {clicked ? (
            <p className="app-dl__note">
              When the download finishes, open <b>{release.file}</b> and tap <b>Install</b>. If Android asks, allow
              your browser to “install unknown apps”.
            </p>
          ) : !onAndroid ? (
            <p className="app-dl__note">
              <Smartphone size={14} aria-hidden /> Open this page on your Android phone to install the app.
            </p>
          ) : null}

          <p className="app-dl__meta">
            <ShieldCheck size={13} aria-hidden /> Signed release · SHA-256{" "}
            <code title={release.sha256}>{release.sha256.slice(0, 16)}…</code>
          </p>
        </>
      )}

      {canInstall && (
        <button type="button" className="app-dl__secondary" onClick={() => promptInstall()}>
          <MonitorDown size={18} aria-hidden /> Install web app
        </button>
      )}

      {showIOSHint && (
        <p className="app-dl__note">
          On iPhone/iPad: tap <b>Share</b> then <b>Add to Home Screen</b> to install TRAVNOK CRM.
        </p>
      )}
    </div>
  );
}
